import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Users, Calendar, UserPlus, Cpu, ArrowRight, Sparkles } from 'lucide-react';
import { api } from '../services/api';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import DashboardStatsCards from '../components/dashboard/DashboardStatsCards';
import DashboardAttendanceCard from '../components/dashboard/DashboardAttendanceCard';
import DashboardActivityCard from '../components/dashboard/DashboardActivityCard';
import DashboardTrendChart from '../components/dashboard/DashboardTrendChart';
import ContractExpiryWidget from '../components/dashboard/ContractExpiryWidget';
import { Skeleton } from '../components/ui/skeleton';

const SETUP_STEPS = [
  { icon: UserPlus, labelKey: 'dashboard.setup.addEmployee', href: '/employees', accent: '#5e6ad2' },
  { icon: Calendar, labelKey: 'dashboard.setup.configSchedules', href: '/planning', accent: '#27a644' },
  { icon: Users, labelKey: 'dashboard.setup.inviteHR', href: '/security-roles', accent: '#d97706' },
  { icon: Cpu, labelKey: 'dashboard.setup.configPayroll', href: '/paie', accent: '#828fff' },
];

const OnboardingEmptyState = ({ t }) => {
  const navigate = useNavigate();
  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid var(--lm-border)", background: "var(--lm-s1)" }}>
      {/* Header band */}
      <div className="px-7 py-6" style={{ borderBottom: "1px solid var(--lm-border)", background: "linear-gradient(135deg, rgba(94,106,210,0.06) 0%, transparent 60%)" }}>
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(94,106,210,0.12)", border: "1px solid rgba(94,106,210,0.25)" }}>
            <Sparkles className="w-5 h-5" style={{ color: "#828fff" }} />
          </div>
          <div>
            <h2 className="text-base font-semibold mb-0.5" style={{ color: "var(--lm-ink)", letterSpacing: "-0.3px" }}>
              {t('dashboard.setup.title')}
            </h2>
            <p className="text-sm" style={{ color: "var(--lm-ink-3)" }}>
              {t('dashboard.setup.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="p-5 grid gap-3 sm:grid-cols-2">
        {SETUP_STEPS.map(({ icon: Icon, labelKey, href, accent }) => (
          <button
            key={href}
            onClick={() => navigate(href)}
            className="flex items-center gap-4 p-4 rounded-xl text-left transition-all group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{ background: "var(--lm-s2)", border: "1px solid var(--lm-border)", '--tw-ring-color': accent, '--tw-ring-offset-color': 'var(--lm-canvas)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.background = `rgba(${accent === '#5e6ad2' ? '94,106,210' : accent === '#27a644' ? '39,166,68' : accent === '#d97706' ? '217,119,6' : '130,143,255'},0.06)`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--lm-border)"; e.currentTarget.style.background = "var(--lm-s2)"; }}
          >
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${accent}18`, border: `1px solid ${accent}30` }}>
              <Icon className="w-4 h-4" style={{ color: accent }} />
            </div>
            <span className="text-sm font-medium flex-1" style={{ color: "var(--lm-ink-2)" }}>
              {t(labelKey)}
            </span>
            <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: accent }} />
          </button>
        ))}
      </div>
    </div>
  );
};

const fetchStats = () =>
  api.get('v1/dashboard/statistics').then((r) => (r.data?.success ? r.data.data : null)).catch(() => null);

const fetchAttendances = () =>
  api.get('v1/dashboard/attendances').then((r) => (r.data?.success ? r.data.data : [])).catch(() => []);

const fetchTrend = () =>
  api.get('v1/dashboard/attendance-trend?days=7').then((r) => (r.data?.success ? r.data.data : [])).catch(() => []);

const DEFAULT_STATS = { totalEmployees: 0, presentToday: 0, avgWorkingHours: 0, lateArrivals: 0, absentToday: 0, pendingApprovals: 0 };

const Dashboard = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [statsQ, attendancesQ, trendQ] = useQueries({
    queries: [
      { queryKey: ['dashboard', 'statistics'], queryFn: fetchStats, staleTime: 60_000 },
      { queryKey: ['dashboard', 'attendances'], queryFn: fetchAttendances, staleTime: 30_000 },
      { queryKey: ['dashboard', 'trend'], queryFn: fetchTrend, staleTime: 60_000 },
    ],
  });

  const loading = statsQ.isPending || attendancesQ.isPending || trendQ.isPending;
  const refreshing = statsQ.isFetching || attendancesQ.isFetching || trendQ.isFetching;

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const stats = statsQ.data ?? DEFAULT_STATS;
  const recentPointages = attendancesQ.data ?? [];
  const trend = trendQ.data ?? [];

  const attendanceRate = stats.totalEmployees > 0
    ? Math.round((stats.presentToday / stats.totalEmployees) * 100)
    : 0;

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-9 w-24" />
        </div>
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-lg border p-4 space-y-3" style={{ borderColor: "var(--lm-border)", background: "var(--lm-s1)" }}>
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-8 w-8 rounded-full" />
              </div>
              <Skeleton className="h-8 w-14" />
            </div>
          ))}
        </div>
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-7">
          <Skeleton className="lg:col-span-4 h-64 rounded-lg" />
          <Skeleton className="lg:col-span-3 h-64 rounded-lg" />
        </div>
      </div>
    );
  }

  const isNewTenant = stats.totalEmployees === 0;

  return (
    <div className="space-y-8 transition-all duration-700 opacity-100 translate-y-0">
      <DashboardHeader t={t} refreshing={refreshing} fetchDashboardData={handleRefresh} />

      <DashboardStatsCards t={t} stats={stats} attendanceRate={attendanceRate} />
      {isNewTenant && <OnboardingEmptyState t={t} />}
      {!isNewTenant && (
        <>
          <ContractExpiryWidget />
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-7">
            <DashboardTrendChart t={t} trend={trend} />
            <DashboardActivityCard t={t} stats={stats} recentPointages={recentPointages} />
          </div>
          <DashboardAttendanceCard t={t} recentPointages={recentPointages} />
        </>
      )}
    </div>
  );
};

export default Dashboard;
